import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { KeyRound } from "lucide-react";
import type { Artist } from "../data";
import { ContentPanel } from "./shared";
import PasswordGate from "./PasswordGate";

interface Props {
  email: string;
  selectedArtist: Artist;
}

interface ArtistDetailsFormProps {
  selectedArtist: Artist;
}

export function ArtistDetailsForm({ selectedArtist }: ArtistDetailsFormProps) {
  const updateArtistDetails = useMutation(api.mutations.updateArtistDetails);

  const [name, setName] = useState(selectedArtist.name);
  const [stageName, setStageName] = useState(selectedArtist.stageName ?? "");
  const [legalName, setLegalName] = useState(selectedArtist.legalName ?? "");
  const [ipiNumber, setIpiNumber] = useState(selectedArtist.ipiNumber ?? "");
  const [phone, setPhone] = useState(selectedArtist.phone ?? "");
  const [links, setLinks] = useState({
    appleMusic: selectedArtist.streamingLinks?.appleMusic ?? "",
    instagram: selectedArtist.streamingLinks?.instagram ?? "",
    soundCloud: selectedArtist.streamingLinks?.soundCloud ?? "",
    spotify: selectedArtist.streamingLinks?.spotify ?? "",
    website: selectedArtist.streamingLinks?.website ?? "",
    youtube: selectedArtist.streamingLinks?.youtube ?? "",
  });
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    setName(selectedArtist.name);
    setStageName(selectedArtist.stageName ?? "");
    setLegalName(selectedArtist.legalName ?? "");
    setIpiNumber(selectedArtist.ipiNumber ?? "");
    setPhone(selectedArtist.phone ?? "");
    setLinks({
      appleMusic: selectedArtist.streamingLinks?.appleMusic ?? "",
      instagram: selectedArtist.streamingLinks?.instagram ?? "",
      soundCloud: selectedArtist.streamingLinks?.soundCloud ?? "",
      spotify: selectedArtist.streamingLinks?.spotify ?? "",
      website: selectedArtist.streamingLinks?.website ?? "",
      youtube: selectedArtist.streamingLinks?.youtube ?? "",
    });
    setStatus("idle");
  }, [selectedArtist]);

  function updateLink(key: string, value: string) {
    setLinks((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("saving");
    try {
      await updateArtistDetails({
        artistId: selectedArtist.id as any,
        ipiNumber,
        legalName,
        name,
        phone,
        stageName,
        streamingLinks: links,
      });
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <p className="muted-copy">IPI is optional and only visible inside this portal account.</p>
      <form className="details-form" onSubmit={handleSubmit}>
          <label>
            <span>Name</span>
            <input onChange={(e) => setName(e.target.value)} placeholder="Account name" value={name} />
          </label>
          <label>
            <span>Artist name</span>
            <input onChange={(e) => setStageName(e.target.value)} placeholder="Stage or artist name" value={stageName} />
          </label>
          <label>
            <span>Legal name</span>
            <input onChange={(e) => setLegalName(e.target.value)} placeholder="Optional" value={legalName} />
          </label>
          <label>
            <span>IPI number (optional)</span>
            <input onChange={(e) => setIpiNumber(e.target.value)} placeholder="Optional publishing identifier" value={ipiNumber} />
          </label>
          <label>
            <span>Phone</span>
            <input onChange={(e) => setPhone(e.target.value)} placeholder="Optional" value={phone} />
          </label>
          <label>
            <span>SoundCloud</span>
            <input onChange={(e) => updateLink("soundCloud", e.target.value)} placeholder="https://soundcloud.com/..." value={links.soundCloud ?? ""} />
          </label>
          <label>
            <span>Spotify</span>
            <input onChange={(e) => updateLink("spotify", e.target.value)} placeholder="https://open.spotify.com/artist/..." value={links.spotify ?? ""} />
          </label>
          <label>
            <span>Apple Music</span>
            <input onChange={(e) => updateLink("appleMusic", e.target.value)} placeholder="https://music.apple.com/..." value={links.appleMusic ?? ""} />
          </label>
          <label>
            <span>YouTube</span>
            <input onChange={(e) => updateLink("youtube", e.target.value)} placeholder="https://youtube.com/..." value={links.youtube ?? ""} />
          </label>
          <label>
            <span>Instagram</span>
            <input onChange={(e) => updateLink("instagram", e.target.value)} placeholder="https://instagram.com/..." value={links.instagram ?? ""} />
          </label>
          <label>
            <span>Website</span>
            <input onChange={(e) => updateLink("website", e.target.value)} placeholder="https://..." value={links.website ?? ""} />
          </label>
          <div className="stem-form-actions details-form-actions">
            {status === "saved" ? <span>Details saved</span> : null}
            {status === "error" ? <span>Could not save details</span> : null}
            <button disabled={status === "saving"} type="submit">
              <KeyRound className="h-4 w-4" />
              {status === "saving" ? "Saving..." : "Save"}
            </button>
          </div>
      </form>
    </>
  );
}

export default function ArtistSettings({ email, selectedArtist }: Props) {
  return (
    <div className="space-stack">
      <ContentPanel title="Account" eyebrow={selectedArtist.name}>
        <div className="settings-list">
          <div>
            <strong>Artist</strong>
            <span>{selectedArtist.stageName || selectedArtist.name}</span>
          </div>
          <div>
            <strong>Email</strong>
            <span>{selectedArtist.email ?? email ?? "Not linked yet"}</span>
          </div>
        </div>
      </ContentPanel>

      <ContentPanel title="Details" eyebrow="Artist profile">
        <ArtistDetailsForm selectedArtist={selectedArtist} />
      </ContentPanel>

      <PasswordGate description="Update the password used for this portal account." />
    </div>
  );
}
